import * as React from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Form, Input, Select } from 'antd';
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import ErrorSummary from 'app/pages/DiscountEditor/components/ErrorSummary';
import {
  getPublicName,
  getInternalName,
  getDescription,
  getDiscountType,
  getPromotionCode
} from 'app/pages/DiscountEditor/modules/BasicInfo';
import {
  changeInternalName,
  changePublicName,
  changeDescription,
  changeDiscountType,
  changePromotionCode
} from 'app/pages/DiscountEditor/actions';
import { DiscountType } from 'app/shared/model/DiscountType';
import { getSectionError } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveDiscountErrorCategory } from 'app/shared/model/SaveDiscountResultDto';
import { validatePromotionCodeFormat } from 'app/shared/validation/promotionCode';

const { TextArea } = Input;
const Option = Select.Option;

type BasicInfoEditorProps = StateProps & DispatchProps;

interface StateProps {
  errorMessage?: string;
  internalName: string;
  publicName: string;
  description: string;
  discountType: DiscountType;
  promotionCode: string;
}

interface DispatchProps {
  onInternalNameChanged: (value: string) => void;
  onPublicNameChanged: (value: string) => void;
  onDescriptionChanged: (value: string) => void;
  onChangeDiscountType: (value: DiscountType) => void;
  onPromotionCodeChanged: (value: string) => void;
}

const BasicInfoEditorView: React.FC<BasicInfoEditorProps> = ({
  errorMessage,
  internalName,
  publicName,
  description,
  discountType,
  promotionCode,
  onInternalNameChanged,
  onPublicNameChanged,
  onDescriptionChanged,
  onChangeDiscountType,
  onPromotionCodeChanged
}) => {
  const promotionFormatError =
    discountType === DiscountType.specialOffer ? validatePromotionCodeFormat(promotionCode) : undefined;

  const onPromotionInputChange = (raw: string) => {
    const alphanumericOnly = raw.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
    onPromotionCodeChanged(alphanumericOnly);
  };

  return (
    <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
      <ComponentHeader text="Basic information" />
      <div style={{ padding: '0 16px 16px' }}>
        <ErrorSummary errorMessage={errorMessage} />
        <Form layout="vertical">
          <Form.Item label="Type">
            <Select
              value={discountType}
              onChange={(value: DiscountType) => onChangeDiscountType(value as DiscountType)}
              placeholder="Select discount type"
            >
              <Option value={DiscountType.marketDiscount}>Market discount</Option>
              <Option value={DiscountType.specialOffer}>Special offer</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Internal name" required>
            <Input
              value={internalName}
              onChange={(e: { target: { value: string } }) => onInternalNameChanged(e.target.value)}
              placeholder="Enter internal name"
            />
          </Form.Item>
          <Form.Item label="Public name" required>
            <Input
              value={publicName}
              onChange={(e: { target: { value: string } }) => onPublicNameChanged(e.target.value)}
              placeholder="Enter public name"
            />
          </Form.Item>
          {discountType === DiscountType.specialOffer ? (
            <Form.Item
              label="Promotion code"
              validateStatus={promotionFormatError ? 'error' : undefined}
              help={promotionFormatError}
              extra="Leave blank for Special Offer to apply without promo code"
            >
              <Input
                value={promotionCode}
                onChange={(e: { target: { value: string } }) => onPromotionInputChange(e.target.value)}
                placeholder="4–15 letters or numbers (optional)"
                maxLength={15}
                autoComplete="off"
              />
            </Form.Item>
          ) : null}
          <Form.Item label="Description">
            <TextArea
              rows={4}
              value={description}
              onChange={(e: { target: { value: string } }) => onDescriptionChanged(e.target.value)}
              placeholder="Enter description"
            />
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  internalName: getInternalName(state),
  publicName: getPublicName(state),
  description: getDescription(state),
  discountType: getDiscountType(state),
  promotionCode: getPromotionCode(state),
  errorMessage: getSectionError(state, SaveDiscountErrorCategory.BasicInfo)
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>): DispatchProps => ({
  onInternalNameChanged: (value) => dispatch(changeInternalName(value)),
  onPublicNameChanged: (value) => dispatch(changePublicName(value)),
  onDescriptionChanged: (value) => dispatch(changeDescription(value)),
  onChangeDiscountType: (value) => dispatch(changeDiscountType(value)),
  onPromotionCodeChanged: (value) => dispatch(changePromotionCode(value))
});

export default connect(mapStateToProps, mapDispatchToProps)(BasicInfoEditorView);
